import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { type IGroupedLog, type ILog } from "src/interfaces/ChecklistLogbooks";
import { HomeLogbooks } from "src/pages/Home/Home.styled";
import { Flex } from "src/styled";

import { groupLogbooks } from "../CheckList.utils";
import { useChecklistsContext } from "../CheckListsContext";
import ChecklistLogbookCard from "./ChecklisLogbookCard";
import { BackButton } from "src/components/shared/BackButton/BackButton";
import { Text } from "@mantine/core";

const ChecklistLogbooks = () => {
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const { logbooks, fetchChecklists } = useChecklistsContext();
  const groupedLogs = useMemo(
    () => groupLogbooks(logbooks as ILog[]),
    [logbooks],
  );

  const selectedLogbook = useMemo(() => {
    if (!collectionId) return null;
    return groupedLogs.find(l => l.value === collectionId) ?? null;
  }, [collectionId, groupedLogs]);

  const handleSelectLogbook = useCallback(
    (name: string) => {
      const listToLookIn = selectedLogbook?.subSections ?? groupedLogs;
      const logbook = listToLookIn.find(l => l.label === name) as IGroupedLog;
      if (!logbook) return;

      if (logbook.subSections && logbook.subSections.length > 0) {
        navigate(`./collection/${logbook.value}`);
        return;
      }

      if (collectionId) {
        navigate(`../logbook/${collectionId}/${logbook.value}`);
        return;
      }

      navigate(`./logbook/${logbook.value}`);
    },
    [collectionId, groupedLogs, navigate, selectedLogbook],
  );

  const renderLogbooks = useCallback(() => {
    return selectedLogbook === null
      ? groupedLogs.map((l, i) => (
          <ChecklistLogbookCard
            key={`checklist-lb-1-${i}`}
            {...l}
            onClick={handleSelectLogbook}
          />
        ))
      : selectedLogbook.subSections.map((l, i) => (
          <ChecklistLogbookCard
            key={`checklist-lb-2-${i}`}
            {...l}
            onClick={handleSelectLogbook}
          />
        ));
  }, [groupedLogs, handleSelectLogbook, selectedLogbook]);

  return (
    <Flex direction="column">
      {selectedLogbook && selectedLogbook?.subSections.length > 0 ? (
        <Flex align="center">
          <BackButton onClick={() => navigate("..")} />
          <Text ml={15}>{selectedLogbook.label}</Text>
        </Flex>
      ) : (
        <Flex align="center" h={34}>
          <Text ml={15}>All Logbooks</Text>
        </Flex>
      )}
      <HomeLogbooks>
        {(selectedLogbook === null || selectedLogbook.subSections.length > 0) &&
          renderLogbooks()}
      </HomeLogbooks>
    </Flex>
  );
};
export default ChecklistLogbooks;
